import React, { useState } from "react";
import {
  useGetOrganisationSystemWorkflowsQuery,
  useInstallOrganisationSystemWorkflowsMutation,
  useInstallOrganisationSystemWorkflowMutation,
} from "@/store/admin_api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorMessage } from "@/util/apiError";
import { SystemWorkflowStatus } from "@/model/interface/SystemWorkflow";
import { toast } from "sonner";

interface SystemWorkflowsCardProps {
  organisationId: string;
}

export default function SystemWorkflowsCard({ organisationId }: SystemWorkflowsCardProps) {
  const { data: workflows = [], isLoading } = useGetOrganisationSystemWorkflowsQuery(organisationId);
  const [installWorkflows, { isLoading: isInstalling }] = useInstallOrganisationSystemWorkflowsMutation();
  const [installWorkflow, { isLoading: isInstallingOne }] = useInstallOrganisationSystemWorkflowMutation();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [workflowToUpdate, setWorkflowToUpdate] = useState<SystemWorkflowStatus | null>(null);

  const missingCount = workflows.filter((workflow) => workflow.installedVersion == null).length;
  const outdatedCount = workflows.filter((workflow) => workflow.outdated).length;

  async function installOne(workflowKey: string, override: boolean) {
    try {
      await installWorkflow({ organisationId, workflowKey, override }).unwrap();
      toast.success(override ? "Workflow updated" : "Workflow installed");
      setWorkflowToUpdate(null);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to install the workflow"));
    }
  }

  async function install(override: boolean) {
    try {
      await installWorkflows({ organisationId, override }).unwrap();
      toast.success(override ? "System workflows reinstalled" : "Missing system workflows installed");
      setOverrideOpen(false);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to install system workflows"));
    }
  }

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>System workflows</CardTitle>
        <CardDescription>
          Workflows the platform maintains. Installing adds any this organisation is missing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="divide-y rounded-lg border">
          {workflows.map((workflow) => (
            <div key={workflow.key} className="flex items-start justify-between gap-4 p-4">
              <div className="space-y-1">
                <p className="font-medium">{workflow.name}</p>
                <p className="text-sm text-muted-foreground">{workflow.description}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <div className="text-right">
                  {workflow.installedVersion == null ? (
                    <Badge variant="outline">Not installed</Badge>
                  ) : workflow.outdated ? (
                    <Badge className="bg-warning text-warning-foreground">Outdated</Badge>
                  ) : (
                    <Badge variant="secondary">Up to date</Badge>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                    {workflow.installedVersion == null
                      ? `v${workflow.currentVersion} available`
                      : `v${workflow.installedVersion} of v${workflow.currentVersion}`}
                  </p>
                </div>

                {workflow.installedVersion == null && (
                  <Button size="sm" onClick={() => installOne(workflow.key, false)} disabled={isInstallingOne}>
                    Install
                  </Button>
                )}
                {workflow.outdated && (
                  <Button size="sm" variant="outline" onClick={() => setWorkflowToUpdate(workflow)}>
                    Update
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => install(false)} disabled={isInstalling || missingCount === 0}>
            {missingCount === 0 ? "Nothing missing" : `Install ${missingCount} missing`}
          </Button>
          <Button variant="outline" onClick={() => setOverrideOpen(true)} disabled={isInstalling || outdatedCount === 0}>
            {outdatedCount === 0 ? "Nothing outdated" : `Update ${outdatedCount} outdated`}
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={workflowToUpdate !== null} onOpenChange={(open) => !open && setWorkflowToUpdate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update "{workflowToUpdate?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces its name, trigger and every action with version {workflowToUpdate?.currentVersion} of the
              platform definition, up from {workflowToUpdate?.installedVersion}. Any changes this organisation made to
              it are lost. Past runs are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isInstallingOne}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => workflowToUpdate && installOne(workflowToUpdate.key, true)}
              disabled={isInstallingOne}
            >
              {isInstallingOne ? "Updating..." : "Overwrite"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite outdated workflows?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the name, trigger and every action on {outdatedCount} workflow
              {outdatedCount === 1 ? "" : "s"} with the current platform definition. Any changes this organisation made
              to them are lost. Past runs are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isInstalling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => install(true)} disabled={isInstalling}>
              {isInstalling ? "Updating..." : "Overwrite"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
